import { useMemo } from 'react'
import { Icon } from '@iconify/react'
import { Select } from '@/components/Select'
import {
  describeActiveStatusFilter,
  filterValueFromStatuses,
  PEDIDOS_STATUS_FILTER_OPTIONS,
  statusesFromFilterValue,
  type PedidosListFiltersState,
  type PedidosStatusFilterValue,
} from '@/lib/pedidos-filters'
import styles from './PedidosFiltersBar.module.css'

export interface PedidosFiltersBarProps {
  value: PedidosListFiltersState
  onChange: (next: PedidosListFiltersState) => void
  onClear: () => void
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  isRefreshing?: boolean
}

export function PedidosFiltersBar({
  value,
  onChange,
  onClear,
  searchQuery,
  onSearchQueryChange,
  isRefreshing = false,
}: PedidosFiltersBarProps) {
  const statusValue = useMemo(
    () => filterValueFromStatuses(value.statuses),
    [value.statuses]
  )

  const statusSummary = useMemo(
    () => describeActiveStatusFilter(value.statuses),
    [value.statuses]
  )

  const handleStatusChange = (next: string) => {
    onChange({
      ...value,
      statuses: statusesFromFilterValue(next as PedidosStatusFilterValue),
    })
  }

  const hasDateRange = value.dateFrom !== '' || value.dateTo !== ''
  const hasExtraFilters =
    hasDateRange || searchQuery.trim() !== '' || statusValue !== 'paid'

  return (
    <section className={styles.panel} aria-label="Filtros da lista de pedidos">
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <Icon icon="mdi:filter-variant" className={styles.headIcon} aria-hidden />
          <h2 className={styles.title}>Filtros</h2>
          <span className={styles.badge} aria-live="polite">
            {statusSummary}
          </span>
        </div>
        <button type="button" className={styles.btnClear} onClick={onClear}>
          Limpar filtros
        </button>
      </div>

      {statusValue === 'paid' ? (
        <p className={styles.filterHint} role="note">
          Filtro padrão: pedidos pagos. Você pode alterar o status de qualquer pedido
          (com motivo e senha).
        </p>
      ) : null}

      <div className={styles.body}>
        <div className={styles.searchRow}>
          <label className={styles.searchField} htmlFor="pedidos-search-input">
            <span className={styles.fieldLabel}>Buscar</span>
            <div className={styles.searchInputWrap}>
              <Icon icon="mdi:magnify" className={styles.searchIcon} aria-hidden />
              <input
                id="pedidos-search-input"
                type="search"
                className={styles.searchInput}
                placeholder="ID do pedido, nome ou e-mail"
                value={searchQuery}
                onChange={(event) => onSearchQueryChange(event.target.value)}
                autoComplete="off"
              />
            </div>
          </label>
        </div>

        <div className={styles.controls}>
          <div className={styles.cell}>
            <Select
              id="pedidos-status-filter"
              className={styles.pedidosSelect}
              label="Status do pedido"
              value={statusValue}
              onChange={handleStatusChange}
              options={[...PEDIDOS_STATUS_FILTER_OPTIONS]}
              placeholder="Selecione o status"
              icon="mdi:list-status"
            />
          </div>

          <div className={styles.cell}>
            <span className={styles.fieldLabel}>Período</span>
            <div className={styles.datePair}>
              <input
                type="date"
                className={styles.dateInput}
                value={value.dateFrom}
                onChange={(event) =>
                  onChange({ ...value, dateFrom: event.target.value })
                }
                aria-label="Data inicial"
              />
              <span className={styles.dateSep} aria-hidden>
                até
              </span>
              <input
                type="date"
                className={styles.dateInput}
                value={value.dateTo}
                onChange={(event) =>
                  onChange({ ...value, dateTo: event.target.value })
                }
                aria-label="Data final"
              />
            </div>
          </div>

          <div
            className={styles.cell}
            title='Com "Pagamento", pedidos não confirmados ficam de fora do intervalo.'
          >
            <span className={styles.fieldLabel}>Referência do período</span>
            <div className={styles.segmented} role="group" aria-label="Referência do período">
              <button
                type="button"
                className={styles.seg}
                data-on={value.dateBasis === 'paid' ? 'true' : 'false'}
                aria-pressed={value.dateBasis === 'paid'}
                onClick={() => onChange({ ...value, dateBasis: 'paid' })}
              >
                Pagamento
              </button>
              <button
                type="button"
                className={styles.seg}
                data-on={value.dateBasis === 'created' ? 'true' : 'false'}
                aria-pressed={value.dateBasis === 'created'}
                onClick={() => onChange({ ...value, dateBasis: 'created' })}
              >
                Criação
              </button>
            </div>
          </div>
        </div>

        <p className={styles.autoApplyNote} aria-live="polite">
          {isRefreshing ? (
            <>
              <Icon icon="mdi:loading" className={styles.refreshIcon} aria-hidden />
              Atualizando lista…
            </>
          ) : (
            <>
              <Icon icon="mdi:check-circle-outline" className={styles.appliedIcon} aria-hidden />
              {hasExtraFilters
                ? 'Filtros aplicados. A lista atualiza automaticamente ao alterar qualquer opção.'
                : 'Exibindo pedidos pagos da campanha. Altere os filtros para refinar a busca.'}
            </>
          )}
        </p>
      </div>
    </section>
  )
}
